/**
 * Add User Drawer
 * Form for creating new users
 */

import { useEffect, useState } from 'react'
import { useTranslation, useForm, Controller, zodResolver, cleanSaudiPhoneInput, generateRandomPassword } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SaudiPhoneInput,
  cn,
  UserPlus,
  Save,
  X,
  Eye,
  EyeOff,
  RefreshCw,
} from '@spcc/uikit'
import {
  userFormSchema,
  type UserFormData,
  getDefaultValues,
  formDataToCreateRequest,
  USER_FORM_FIELDS,
  USER_FORM_T_KEYS,
  INVITABLE_ROLE_OPTIONS,
  ALL_PLANTS_VALUE,
  type InvitableRole,
  roleRequiresFactory,
  roleAllowsLocationOption,
} from '../../domain/utils/user-form-schema'
import type { CreateUserRequest } from '../../domain/types'

export interface InviteFactoryOption {
  id: string
  name: string
  code?: string
}

export interface InviteUserDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateUserRequest) => void
  roleOptions?: Array<{ value: InvitableRole; labelKey: string; label: string }>
  factoryOptions?: InviteFactoryOption[]
  locationOptions?: InviteFactoryOption[]  // Reuse same type: { id, name, code }
  isLoading?: boolean
  isRTL?: boolean
}

function formatFactoryLabel(factory: InviteFactoryOption): string {
  if (!factory.code) return factory.name
  return `${factory.name} (${factory.code})`
}

/**
 * Plant and location are separate relations on the backend but a single
 * "assignment" concept in the UI — merge into one dropdown with prefixed
 * values so we know which field to set on selection.
 */
const FACTORY_PREFIX = 'factory:'
const LOCATION_PREFIX = 'location:'

export function getCombinedAssignmentValue(factoryId?: string, locationId?: string): string {
  if (factoryId) return `${FACTORY_PREFIX}${factoryId}`
  if (locationId) return `${LOCATION_PREFIX}${locationId}`
  return ALL_PLANTS_VALUE
}

export function parseCombinedAssignmentValue(value: string): { factoryId: string; locationId: string } {
  if (value.startsWith(FACTORY_PREFIX)) {
    return { factoryId: value.slice(FACTORY_PREFIX.length), locationId: '' }
  }
  if (value.startsWith(LOCATION_PREFIX)) {
    return { factoryId: '', locationId: value.slice(LOCATION_PREFIX.length) }
  }
  return { factoryId: '', locationId: '' }
}

// ===========================================
// Component
// ===========================================

export function InviteUserDrawer({
  open,
  onOpenChange,
  onSubmit,
  roleOptions = INVITABLE_ROLE_OPTIONS,
  factoryOptions = [],
  locationOptions = [],
  isLoading = false,
  isRTL = false,
}: InviteUserDrawerProps) {
  const { t } = useTranslation()

  const [showPassword, setShowPassword] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: getDefaultValues(),
  })
  const selectedRole = watch('role')
  const requiresFactory = roleRequiresFactory(selectedRole)
  const showLocationOptions = roleAllowsLocationOption(selectedRole)

  // Reset form when drawer opens and auto-generate a password
  useEffect(() => {
    if (open) {
      reset({ ...getDefaultValues(), password: generateRandomPassword() })
      setShowPassword(false)
    }
  }, [open, reset])

  const onFormSubmit = (data: UserFormData) => {
    const request = formDataToCreateRequest(data)
    onSubmit(request)
  }

  const handleSave = handleSubmit(onFormSubmit)

  // Header gradient - emerald for add user flow
  const headerGradient = 'from-emerald-600 to-emerald-700'

  const headerContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <UserPlus className="w-6 h-6 text-white" />
      </div>
      <div className={cn(isRTL && 'text-right')}>
        <h2 className="text-lg font-bold text-white">
          {t('admin.user.form.addTitle', 'Add User')}
        </h2>
        <p className="text-sm text-white/80">
          {t('admin.user.form.addDescription', 'Create a user account and assign access')}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className={cn('flex gap-3')}>
      <Button
        type="button"
        variant="outline"
        className="flex-1 h-12 rounded-xl"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        <X className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button
        type="button"
        className="flex-1 h-12 rounded-xl"
        disabled={isLoading}
        onClick={handleSave}
      >
        <Save className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {isLoading
          ? t('common.creating', 'Creating...')
          : t('admin.user.form.createUser', 'Create User')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={headerGradient}
      headerContent={headerContent}
      footerContent={footerContent}
      fixedWidth={480}
      title={t('admin.user.form.addTitle', 'Add User')}
    >
      <form id="invite-user-form" onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* User Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('admin.user.form.userInfo', 'User Information')}
          </h3>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.NAME}>
              {t(USER_FORM_T_KEYS.LABELS.NAME, 'Full Name')} *
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id={USER_FORM_FIELDS.NAME}
                    value={value ?? ''}
                    placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.NAME, 'Enter full name')}
                    className={cn('h-11 rounded-xl', errors.name && 'border-destructive')}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{t(errors.name.message || '')}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.EMAIL}>
              {t(USER_FORM_T_KEYS.LABELS.EMAIL, 'Email Address')} *
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id={USER_FORM_FIELDS.EMAIL}
                    type="email"
                    value={value ?? ''}
                    placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.EMAIL, 'user@example.com')}
                    className={cn('h-11 rounded-xl', errors.email && 'border-destructive')}
                    dir="ltr"
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{t(errors.email.message || '')}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={USER_FORM_FIELDS.PASSWORD}>
                {t(USER_FORM_T_KEYS.LABELS.PASSWORD, 'Password')} *
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setValue('password', generateRandomPassword())}
              >
                <RefreshCw className="w-3 h-3 me-1" />
                {t('admin.user.form.regenerate', 'Regenerate')}
              </Button>
            </div>
            <Controller
              name="password"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <div className="relative">
                    <Input
                      id={USER_FORM_FIELDS.PASSWORD}
                      type={showPassword ? 'text' : 'password'}
                      value={value ?? ''}
                      placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.PASSWORD, 'Enter password')}
                      className={cn('h-11 rounded-xl pe-10', errors.password && 'border-destructive')}
                      dir="ltr"
                      {...fieldProps}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute inset-y-0 end-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                )
              }}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{t(errors.password.message || '', 'Password is required')}</p>
            )}
          </div>

          {/* Phone — Saudi +966 + editable digits */}
          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.PHONE}>
              {t(USER_FORM_T_KEYS.LABELS.PHONE, 'Phone Number')}
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <SaudiPhoneInput
                  id={USER_FORM_FIELDS.PHONE}
                  value={cleanSaudiPhoneInput(field.value ?? '')}
                  onChange={(digits) => field.onChange(digits)}
                  placeholder="512345678"
                  className={cn('h-11 rounded-xl', errors.phone && 'border-destructive')}
                  error={errors.phone ? t(errors.phone.message ?? 'validation.phoneSaudiFormat') : undefined}
                />
              )}
            />
          </div>
        </div>

        {/* Role & Assignment */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('admin.user.form.roleAssignment', 'Role & Assignment')}
          </h3>

          {/* Role */}
          <div className="space-y-2">
            <Label>{t(USER_FORM_T_KEYS.LABELS.ROLE, 'Role')} *</Label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger className={cn('h-11 rounded-xl', errors.role && 'border-destructive')}>
                    <SelectValue placeholder={t('admin.user.form.selectRole', 'Select role')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey, option.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.role && (
              <p className="text-xs text-destructive">{t((errors.role?.message as string) || "")}</p>
            )}
          </div>

          {/* Plant / Location (required for kiosk, security guard, etc. — optional otherwise) */}
          <div className="space-y-2">
            <Label>
              {t(USER_FORM_T_KEYS.LABELS.FACTORY, 'Plant / Location')}
              {requiresFactory && ' *'}
            </Label>
            <Select
              value={getCombinedAssignmentValue(watch('factoryId'), watch('locationId'))}
              onValueChange={(value) => {
                const { factoryId, locationId } = parseCombinedAssignmentValue(value)
                setValue('factoryId', factoryId)
                setValue('locationId', locationId)
                trigger(['factoryId', 'locationId'])
              }}
            >
              <SelectTrigger className={cn('h-11 rounded-xl', errors.factoryId && 'border-destructive')}>
                <SelectValue
                  placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.FACTORY, 'All Plants')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PLANTS_VALUE}>
                  {t(USER_FORM_T_KEYS.LABELS.ALL_PLANTS, 'All Plants')}
                </SelectItem>
                {factoryOptions.map((factory) => (
                  <SelectItem key={`factory:${factory.id}`} value={`factory:${factory.id}`}>
                    {formatFactoryLabel(factory)}
                  </SelectItem>
                ))}
                {showLocationOptions && locationOptions.map((location) => (
                  <SelectItem key={`location:${location.id}`} value={`location:${location.id}`}>
                    {formatFactoryLabel(location)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.factoryId && (
              <p className="text-xs text-destructive">
                {t((errors.factoryId?.message as string) || '', 'Plant is required for this role')}
              </p>
            )}
          </div>
        </div>
      </form>
    </BaseDrawer>
  )
}
